import React from 'react';
import { Link } from 'react-router-dom';

import PageHero from '../components/ui/PageHero';
import FadeUp from '../components/ui/FadeUp';
import SectionHeader from '../components/ui/SectionHeader';

import { WHY_ITEMS, BUSINESS } from '../data/siteData';

const MILESTONES = [
  {
    year: '2000',
    title: 'Founded',
    desc: 'Brantford Auto Wrecking & Salvage opens its doors in Paris, ON, with a small yard and a big commitment to honest service.',
  },
  {
    year: '2005',
    title: 'Expanded Inventory',
    desc: 'Grew the yard to 200+ vehicles and hired a full-time parts locator team to serve mechanics and DIYers across Brant County.',
  },
  {
    year: '2010',
    title: 'Eco Certification',
    desc: 'Achieved full environmental certification for responsible fluid handling, metal recycling, and end-of-life vehicle processing.',
  },
  {
    year: '2015',
    title: 'Parts Locator Network',
    desc: 'Joined a province-wide salvage network, giving customers access to parts from hundreds of yards across Ontario.',
  },
  {
    year: '2020',
    title: '20 Years Strong',
    desc: 'Celebrated two decades of serving the community. Over 10,000 customers served and thousands of vehicles responsibly recycled.',
  },
  {
    year: 'Today',
    title: 'Still Growing',
    desc: "500+ vehicles on-site, same-day service, and the same family values that started it all. Call us — we'll find your part.",
  },
];

const TEAM = [
  {
    name: 'M S Zarif',
    role: 'CEO & Founder',
    initials: 'MZ',
    email: 'director@advanceautoworld.com',
    bio: 'Leading Advance Auto Wrecking with a commitment to quality auto parts, honest service, and customer satisfaction.',
  },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        tag="Our Story"
        title="About Brantford Auto"
        subtitle="Family-owned and community-driven since 2000. Two decades of trusted auto salvage in Brant County."
      />

      {/* Mission */}
      <section
        style={{
          padding: '5rem 2rem',
          background: 'white',
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '5rem',
            alignItems: 'center',
          }}
          className="about-grid"
        >
          <FadeUp>
            <SectionHeader
              tag="Our Mission"
              title={
                <>
                  Honest Parts.
                  <br />
                  Fair Prices.
                  <br />
                  Local Roots.
                </>
              }
            />

            <p
              style={{
                fontSize: '1.05rem',
                color: 'var(--text-mid)',
                lineHeight: 1.8,
                marginTop: '1.5rem',
                marginBottom: '1.5rem',
              }}
            >
              We started Brantford Auto Wrecking & Salvage with one goal:
              give drivers and mechanics in our community access to quality
              used parts without the dealership markup or the big-box
              runaround.
            </p>

            <p
              style={{
                fontSize: '1.05rem',
                color: 'var(--text-mid)',
                lineHeight: 1.8,
                marginBottom: '2rem',
              }}
            >
              Over 20 years later, that mission hasn't changed. We're still a
              family operation, still based in Paris, ON, and still committed
              to treating every customer the way we'd want to be treated —
              with honesty, speed, and a fair deal.
            </p>

            <div
              style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              <Link to="/inventory" className="btn-primary">
                🔍 Search Our Inventory
              </Link>

              <Link to="/contact" className="btn-dark">
                📞 Get In Touch
              </Link>
            </div>
          </FadeUp>

          <FadeUp delay={150}>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  borderRadius: 16,
                  overflow: 'hidden',
                  aspectRatio: '4/3',
                }}
              >
                <img
                  src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=800&q=80"
                  alt="Auto yard"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'saturate(0.85)',
                  }}
                />
              </div>

              <div
                style={{
                  position: 'absolute',
                  bottom: '-1.5rem',
                  left: '-1.5rem',
                  background: 'var(--navy)',
                  color: 'white',
                  borderRadius: 12,
                  padding: '1.5rem',
                  boxShadow: '0 8px 32px rgba(13,27,42,0.3)',
                }}
              >
                <div
                  style={{
                    fontFamily: '"Bebas Neue"',
                    fontSize: '2.5rem',
                    color: 'var(--orange)',
                    lineHeight: 1,
                  }}
                >
                  500+
                </div>

                <div
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--silver)',
                  }}
                >
                  Vehicles
                  <br />
                  In Yard
                </div>
              </div>
            </div>
          </FadeUp>
        </div>

        <style>{`
          @media (max-width: 860px) {
            .about-grid {
              grid-template-columns: 1fr !important;
              gap: 3rem !important;
            }
          }
        `}</style>
      </section>

      {/* Timeline */}
      <section
        style={{
          padding: '5rem 2rem',
          background: 'var(--ice)',
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
          }}
        >
          <FadeUp
            style={{
              marginBottom: '3.5rem',
              textAlign: 'center',
            }}
          >
            <SectionHeader
              tag="Our History"
              title="20+ Years in the Making"
              center
            />
          </FadeUp>

          <div
            style={{
              position: 'relative',
              maxWidth: 860,
              margin: '0 auto',
            }}
          >
            {/* Vertical line */}
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: 2,
                background: 'var(--silver-light)',
                transform: 'translateX(-50%)',
              }}
              className="timeline-line"
            />

            {MILESTONES.map((m, i) => (
              <FadeUp key={m.year} delay={i * 80}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 40px 1fr',
                    gap: '1.5rem',
                    alignItems: 'center',
                    marginBottom: '2.5rem',
                  }}
                  className="timeline-row"
                >
                  {/* Left content */}
                  <div
                    style={{
                      textAlign: i % 2 === 0 ? 'right' : 'left',
                    }}
                    className={i % 2 !== 0 ? 'timeline-spacer' : ''}
                  >
                    {i % 2 === 0 ? (
                      <MilestoneCard m={m} />
                    ) : (
                      <span />
                    )}
                  </div>

                  {/* Center dot */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'var(--orange)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: '"Bebas Neue"',
                        fontSize: '0.75rem',
                        color: 'white',
                        letterSpacing: '0.05em',
                        zIndex: 1,
                        flexShrink: 0,
                      }}
                    >
                      {m.year.length > 4 ? '●' : m.year.slice(-2)}
                    </div>
                  </div>

                  {/* Right content */}
                  <div>
                    {i % 2 !== 0 ? (
                      <MilestoneCard m={m} />
                    ) : (
                      <span />
                    )}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <style>{`
          @media (max-width: 640px) {
            .timeline-line {
              left: 20px !important;
            }

            .timeline-row {
              grid-template-columns: 40px 1fr !important;
            }

            .timeline-spacer {
              display: none !important;
            }
          }
        `}</style>
      </section>

      {/* Team */}
      <section
        style={{
          padding: '5rem 2rem',
          background: 'white',
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
          }}
        >
          <FadeUp
            style={{
              textAlign: 'center',
              marginBottom: '3rem',
            }}
          >
            <SectionHeader
              tag="The Team"
              title="The People Behind the Parts"
              center
            />
          </FadeUp>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1.5rem',
            }}
          >
            {TEAM.map((member, i) => (
              <FadeUp key={member.name} delay={i * 80}>
                <div
                  style={{
                    background: 'var(--ice)',
                    borderRadius: 14,
                    padding: '2rem',
                    textAlign: 'center',
                  }}
                >
                  {/* Initials */}
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: 'var(--navy)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: '"Bebas Neue"',
                      fontSize: '1.6rem',
                      color: 'white',
                      margin: '0 auto 1rem',
                    }}
                  >
                    {member.initials}
                  </div>

                  {/* Name */}
                  <h3
                    style={{
                      fontFamily: '"Barlow Condensed"',
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      color: 'var(--navy)',
                      letterSpacing: '0.03em',
                      marginBottom: '0.25rem',
                    }}
                  >
                    {member.name}
                  </h3>

                  {/* Role */}
                  <p
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--orange)',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      marginBottom: '0.75rem',
                    }}
                  >
                    {member.role}
                  </p>

                  {/* Bio */}
                  <p
                    style={{
                      fontSize: '0.9rem',
                      color: 'var(--text-mid)',
                      lineHeight: 1.6,
                      marginBottom: '1rem',
                    }}
                  >
                    {member.bio}
                  </p>

                  {/* Email */}
                  {member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      style={{
                        display: 'inline-block',
                        fontSize: '0.85rem',
                        color: 'var(--orange)',
                        textDecoration: 'none',
                        fontWeight: 600,
                        wordBreak: 'break-word',
                      }}
                    >
                      ✉ {member.email}
                    </a>
                  )}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section
        style={{
          padding: '5rem 2rem',
          background: 'var(--navy)',
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
          }}
        >
          <FadeUp
            style={{
              textAlign: 'center',
              marginBottom: '3rem',
            }}
          >
            <SectionHeader
              tag="What We Stand For"
              title="Our Values"
              light
              center
            />
          </FadeUp>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1.5rem',
            }}
          >
            {WHY_ITEMS.map((item, i) => (
              <FadeUp key={item.title} delay={i * 80}>
                <div
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 12,
                    padding: '2rem',
                  }}
                >
                  <div
                    style={{
                      fontSize: '2rem',
                      marginBottom: '1rem',
                    }}
                  >
                    {item.icon}
                  </div>

                  <h3
                    style={{
                      fontFamily: '"Barlow Condensed"',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      color: 'white',
                      marginBottom: '0.5rem',
                    }}
                  >
                    {item.title}
                  </h3>

                  <p
                    style={{
                      fontSize: '0.88rem',
                      color: 'var(--silver)',
                      lineHeight: 1.6,
                    }}
                  >
                    {item.desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        style={{
          background: 'var(--ice)',
          padding: '4rem 2rem',
          textAlign: 'center',
        }}
      >
        <FadeUp>
          <h2
            style={{
              fontFamily: '"Bebas Neue"',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              color: 'var(--navy)',
              marginBottom: '1rem',
            }}
          >
            Ready to Find Your Part?
          </h2>

          <p
            style={{
              color: 'var(--text-mid)',
              marginBottom: '2rem',
              fontSize: '1.05rem',
            }}
          >
            Search our inventory online or give us a call.
          </p>

          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Link to="/inventory" className="btn-primary">
              🔍 Browse Inventory
            </Link>

            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="btn-dark"
            >
              📞 {BUSINESS.phone}
            </a>
          </div>
        </FadeUp>
      </section>
    </>
  );
}

function MilestoneCard({ m }) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 10,
        padding: '1.25rem 1.5rem',
        boxShadow: '0 2px 16px rgba(13,27,42,0.07)',
      }}
    >
      <div
        style={{
          fontFamily: '"Bebas Neue"',
          fontSize: '1.4rem',
          color: 'var(--orange)',
          letterSpacing: '0.05em',
          marginBottom: '0.25rem',
        }}
      >
        {m.year} — {m.title}
      </div>

      <p
        style={{
          fontSize: '0.88rem',
          color: 'var(--text-mid)',
          lineHeight: 1.6,
        }}
      >
        {m.desc}
      </p>
    </div>
  );
}